import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useSortBy, useTable } from "react-table";
import {
  IOndoHedge,
  IOndoSwap,
  OndoHedgeStatus,
  useOndo,
} from "../../hooks/api/admin/useOndo";
import {
  ONDO_HEDGER_ADDRESS,
  useOndoHedgerBalances,
} from "../../hooks/api/admin/useOndoHedgerBalances";
import { ITableColumn } from "../Table/TableColumn";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import {
  CopyButton,
  TableHeaderCell,
  TableLoader,
  TableWrapper,
} from "../Table/style";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import { MobileDirectory } from "../shared/MobileDirectory";
import useScreenSize from "../../hooks/api/screenSize/useScreenSize";
import { getCollateralLogo } from "../../utils/asset";
import {
  ActionRow,
  AccountMeta,
  AssetDetails,
  AssetLogo,
  AssetLogoFallback,
  AssetMeta,
  AssetName,
  CellStack,
  CellSubtext,
  EmptyState,
  ErrorText,
  Field,
  FiltersWrapper,
  HeaderRow,
  HealthChips,
  Input,
  InventoryAsset,
  InventoryBalance,
  InventoryColumns,
  InventoryList,
  InventoryListHeader,
  InventoryPanel,
  InventoryPanelHeader,
  InventoryRow,
  MutedText,
  NetworkDescription,
  NetworkIdentity,
  NetworkMark,
  NetworkName,
  OverviewHeader,
  OverviewWrapper,
  PageSubtitle,
  PageTitle,
  PageWrapper,
  PaginationButton,
  PaginationControls,
  PaginationRow,
  PaginationSelect,
  PausedAssetName,
  PausedAssetReason,
  PausedAssetRow,
  PausedAssetText,
  PausedAssetsBanner,
  PausedAssetsList,
  PausedAssetsTitle,
  Section,
  SectionHeader,
  SectionTitle,
  SectionTitleStack,
  Select,
  StatusBadge,
} from "./style";

const DEFAULT_ASSET = "";
const DEFAULT_LIMIT = 100;
const DEFAULT_SWAP_PAGE_SIZE = 10;
const SWAP_PAGE_SIZE_OPTIONS = [10, 25, 50];

const STATUS_OPTIONS: { label: string; value: OndoHedgeStatus }[] = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Succeeded", value: "succeeded" },
  { label: "Failed", value: "failed" },
];

const shorten = (value?: string, start = 8, end = 6) => {
  if (!value) {
    return "-";
  }
  if (value.length <= start + end + 3) {
    return value;
  }
  return `${value.slice(0, start)}...${value.slice(-end)}`;
};

const formatDate = (value?: string) => {
  if (!value) {
    return "-";
  }
  const parsed = moment(value);
  if (!parsed.isValid() || parsed.year() <= 1970) {
    return "-";
  }
  return parsed.format("DD MMM YYYY HH:mm:ss");
};

const formatDisplayBalance = (value?: string, precision = 6) => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "-";
  }
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return trimmed;
  }

  const [integer, fraction = ""] = trimmed.split(".");
  const displayFraction = fraction.slice(0, precision).replace(/0+$/, "");
  return displayFraction ? `${integer}.${displayFraction}` : integer;
};

const formatBalanceWithSymbol = (value?: string, symbol?: string) => {
  const balance = formatDisplayBalance(value);
  return balance === "-" ? balance : `${balance} ${symbol}`;
};

const hedgerInventoryLabel = (symbol: string) => {
  switch (symbol) {
    case "ETH":
      return "Transaction gas";
    case "USDC":
      return "Settlement inventory";
    default:
      return "Hedge inventory";
  }
};

const formatUnits = (raw?: string, decimals = 18, precision = 4) => {
  const value = raw?.trim();
  if (!value) {
    return "-";
  }
  if (!/^\d+$/.test(value)) {
    return value;
  }

  const padded = value.padStart(decimals + 1, "0");
  const integer = padded.slice(0, -decimals) || "0";
  const fraction = padded
    .slice(-decimals)
    .slice(0, precision)
    .replace(/0+$/, "");

  return fraction ? `${integer}.${fraction}` : integer;
};

const formatSide = (row: Pick<IOndoSwap, "is_buy" | "asset">) =>
  row.is_buy ? `Buy ${row.asset}` : `Sell ${row.asset}`;

const sortTime = (row: IOndoSwap) =>
  row.accepted_at ||
  row.created_at ||
  row.hedged_at ||
  row.failed_at ||
  row.expires_at ||
  "";

const renderAssetLogo = (asset: string) => {
  const logo = getCollateralLogo(asset);
  if (logo) {
    return <AssetLogo src={logo} alt={`${asset} logo`} />;
  }
  return (
    <AssetLogoFallback>
      {asset.trim().slice(0, 1).toUpperCase() || "?"}
    </AssetLogoFallback>
  );
};

interface IOndoTableProps<T extends object> {
  data: T[];
  columns: ITableColumn<T>[];
  isLoading: boolean;
  emptyMessage: string;
}

function OndoTable<T extends object>({
  data,
  columns,
  isLoading,
  emptyMessage,
}: IOndoTableProps<T>) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
      },
      useSortBy
    );

  return (
    <TableWrapper>
      <TableLoader isLoading={isLoading} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const headerGroupProps = headerGroup.getHeaderGroupProps();
            return (
              <tr {...headerGroupProps} key={headerGroupProps.key}>
                {headerGroup.headers.map((column: any) => {
                  const headerProps = column.getHeaderProps(
                    column.getSortByToggleProps()
                  );
                  return (
                    <TableHeaderCell
                      {...headerProps}
                      key={headerProps.key}
                      style={{ width: column.width }}
                    >
                      {column.render("Header")}
                    </TableHeaderCell>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const rowProps = row.getRowProps();
            return (
              <tr {...rowProps} key={rowProps.key}>
                {row.cells.map((cell) => {
                  const cellProps = cell.getCellProps();
                  return (
                    <td {...cellProps} key={cellProps.key}>
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {!isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </TableWrapper>
  );
}

export function Ondo() {
  const { isMobileScreen } = useScreenSize();
  const { addToast } = useToast();
  const [status, setStatus] = useState<OndoHedgeStatus>("");
  const [asset, setAsset] = useState(DEFAULT_ASSET);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [pauseReason, setPauseReason] = useState("manual pause");
  const [swapPage, setSwapPage] = useState(1);
  const [swapPageSize, setSwapPageSize] = useState(DEFAULT_SWAP_PAGE_SIZE);

  const {
    swaps,
    failedHedges,
    inventory,
    isLoadingSwaps,
    isValidatingSwaps,
    isLoadingFailedHedges,
    isValidatingFailedHedges,
    isLoadingInventory,
    isValidatingInventory,
    swapsError,
    swapsErrorMessage,
    failedHedgesError,
    failedHedgesErrorMessage,
    inventoryError,
    inventoryErrorMessage,
    retryHedge,
    markHedgeResolved,
    pauseAsset,
    resumeAsset,
    refresh,
  } = useOndo({ status, asset, limit });
  const {
    balances: hedgerBalances,
    error: hedgerBalancesError,
    isLoading: isLoadingHedgerBalances,
    isValidating: isValidatingHedgerBalances,
    refresh: refreshHedgerBalances,
  } = useOndoHedgerBalances();

  const onCopy = useCallback(
    async (value?: string) => {
      if (!value) {
        return;
      }
      await navigator.clipboard.writeText(value);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Copied",
          subheader: shorten(value),
          status: ToastStatusEnum.SUCCESS,
        },
        2000
      );
    },
    [addToast]
  );

  const onRetry = useCallback(
    async (hedgeId: string) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Retry Ondo hedge ${hedgeId}?`)) {
        return;
      }
      await retryHedge(hedgeId);
    },
    [retryHedge]
  );

  const onMarkResolved = useCallback(
    async (hedgeId: string) => {
      // eslint-disable-next-line no-alert
      const txHash = window.prompt(
        "Mainnet tx hash or resolution note",
        "manual"
      );
      if (txHash === null) {
        return;
      }
      await markHedgeResolved(hedgeId, txHash.trim() || undefined);
    },
    [markHedgeResolved]
  );

  const onPauseAsset = useCallback(async () => {
    const targetAsset = asset.trim();
    if (!targetAsset) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Pause Ondo quotes for ${targetAsset}?`)) {
      return;
    }
    await pauseAsset(targetAsset, pauseReason.trim() || "manual pause");
  }, [asset, pauseAsset, pauseReason]);

  const onResumeAsset = useCallback(async () => {
    const targetAsset = asset.trim();
    if (!targetAsset) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Resume Ondo quotes for ${targetAsset}?`)) {
      return;
    }
    await resumeAsset(targetAsset);
  }, [asset, resumeAsset]);

  const onResumePausedAsset = useCallback(
    async (targetAsset: string) => {
      if (!targetAsset) {
        return;
      }
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Resume Ondo quotes for ${targetAsset}?`)) {
        return;
      }
      await resumeAsset(targetAsset);
    },
    [resumeAsset]
  );

  const onLimitChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) {
      return;
    }
    setLimit(Math.max(1, Math.min(500, value)));
  }, []);

  const onSwapPageSizeChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = Number(event.target.value);
      if (!Number.isFinite(value)) {
        return;
      }
      setSwapPageSize(value);
      setSwapPage(1);
    },
    []
  );

  const onRefresh = useCallback(async () => {
    await Promise.all([refresh(), refreshHedgerBalances()]);
  }, [refresh, refreshHedgerBalances]);

  const renderIdButton = useCallback(
    (value?: string) => {
      if (!value) {
        return <span>-</span>;
      }
      return (
        <CopyButton
          type="button"
          title={value}
          onClick={(event) => {
            event.stopPropagation();
            onCopy(value);
          }}
        >
          {shorten(value)}
        </CopyButton>
      );
    },
    [onCopy]
  );

  const renderTxHash = useCallback(
    (txHash?: string) => {
      if (!txHash) {
        return <span>-</span>;
      }
      return (
        <CellStack>
          {renderIdButton(txHash)}
          {txHash.startsWith("0x") ? (
            <CellSubtext>
              <a
                href={`https://etherscan.io/tx/${txHash}`}
                rel="noreferrer"
                target="_blank"
              >
                Etherscan
              </a>
            </CellSubtext>
          ) : null}
        </CellStack>
      );
    },
    [renderIdButton]
  );

  const renderRepairActions = useCallback(
    (hedgeId?: string, retryable?: boolean) => {
      if (!hedgeId || !retryable) {
        return <span>-</span>;
      }
      return (
        <ActionRow>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => onRetry(hedgeId)}
            style={{ minHeight: 28, minWidth: 64, padding: "4px 8px" }}
          >
            Retry
          </Button>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => onMarkResolved(hedgeId)}
            style={{ minHeight: 28, minWidth: 88, padding: "4px 8px" }}
          >
            Resolve
          </Button>
        </ActionRow>
      );
    },
    [onMarkResolved, onRetry]
  );

  const inventoryRows = useMemo(
    () =>
      [...(inventory?.inventory ?? [])].sort((left, right) =>
        left.collateral.localeCompare(right.collateral)
      ),
    [inventory?.inventory]
  );

  const sharedUsdcBalance = useMemo(
    () =>
      inventoryRows.find((balance) => balance.usdc_balance.trim())
        ?.usdc_balance,
    [inventoryRows]
  );

  const pausedAssetEntries = useMemo(
    () => Object.entries(inventory?.paused_assets ?? {}),
    [inventory?.paused_assets]
  );

  const sortedHedgerBalances = useMemo(() => {
    const priority: Record<string, number> = { ETH: 0, USDC: 1 };
    return [...hedgerBalances].sort((left, right) => {
      const leftPriority = priority[left.symbol] ?? 2;
      const rightPriority = priority[right.symbol] ?? 2;
      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }
      return left.symbol.localeCompare(right.symbol);
    });
  }, [hedgerBalances]);

  const swapPageCount = useMemo(
    () => Math.max(1, Math.ceil(swaps.length / swapPageSize)),
    [swapPageSize, swaps.length]
  );

  useEffect(() => {
    setSwapPage(1);
  }, [asset, limit, status]);

  useEffect(() => {
    setSwapPage((page) => Math.min(page, swapPageCount));
  }, [swapPageCount]);

  const paginatedSwaps = useMemo(() => {
    const start = (swapPage - 1) * swapPageSize;
    return swaps.slice(start, start + swapPageSize);
  }, [swapPage, swapPageSize, swaps]);

  const swapRangeStart =
    swaps.length === 0 ? 0 : (swapPage - 1) * swapPageSize + 1;
  const swapRangeEnd = Math.min(swaps.length, swapPage * swapPageSize);

  const swapColumns = useMemo<ITableColumn<IOndoSwap>[]>(
    () => [
      {
        id: "time",
        title: "Time",
        accessor: sortTime,
        valueExtractor: formatDate,
      },
      {
        title: "Quote",
        accessor: "quote_id",
        Cell: ({ value }) => renderIdButton(value),
      },
      {
        title: "Order",
        accessor: "order_id",
        Cell: ({ value }) => renderIdButton(value),
      },
      {
        id: "side",
        title: "Side",
        accessor: formatSide,
      },
      {
        title: "Account",
        accessor: "account",
        Cell: ({ value }) => renderIdButton(value),
      },
      {
        title: "Provider",
        accessor: "provider_account",
        Cell: ({ value }) => renderIdButton(value),
      },
      {
        title: "Base",
        accessor: "base_amount",
        align: "right",
        Cell: ({ value, row }) => (
          <CellStack>
            <span>
              {formatUnits(value, 18, 6)} {row.original.asset}
            </span>
            <CellSubtext>{shorten(value, 10, 8)}</CellSubtext>
          </CellStack>
        ),
      },
      {
        title: "Quote",
        accessor: "quote_amount",
        align: "right",
        Cell: ({ value }) => (
          <CellStack>
            <span>{formatUnits(value, 6, 2)} USDC</span>
            <CellSubtext>{shorten(value, 10, 8)}</CellSubtext>
          </CellStack>
        ),
      },
      {
        title: "Price",
        accessor: "price",
        align: "right",
        valueExtractor: (value) => `$${formatUnits(value, 18, 6)}`,
      },
      {
        title: "Quote Status",
        accessor: "quote_status",
        Cell: ({ value }) => (
          <StatusBadge status={value}>{value || "-"}</StatusBadge>
        ),
      },
      {
        title: "Hedge Status",
        accessor: "hedge_status",
        Cell: ({ value }) => (
          <StatusBadge status={value}>{value || "-"}</StatusBadge>
        ),
      },
      {
        title: "Attempts",
        accessor: "attempts",
        align: "right",
        valueExtractor: (value) => String(value ?? 0),
      },
      {
        title: "Tx Hash",
        accessor: "tx_hash",
        Cell: ({ value }) => renderTxHash(value),
      },
      {
        title: "Error",
        accessor: "last_error",
        Cell: ({ value, row }) => (
          <CellStack>
            <span>{value || row.original.cancel_reason || "-"}</span>
          </CellStack>
        ),
      },
      {
        title: "Action",
        accessor: "hedge_id",
        disableSortBy: true,
        Cell: ({ value, row }) =>
          renderRepairActions(value, row.original.retryable),
      },
    ],
    [renderIdButton, renderRepairActions, renderTxHash]
  );

  const failedHedgeColumns = useMemo<ITableColumn<IOndoHedge>[]>(
    () => [
      {
        title: "Failed At",
        accessor: "failed_at",
        valueExtractor: formatDate,
      },
      {
        title: "Reattested",
        accessor: "reattested_at",
        valueExtractor: formatDate,
      },
      {
        title: "Hedge",
        accessor: "hedge_id",
        Cell: ({ value }) => renderIdButton(value),
      },
      {
        title: "Order",
        accessor: "order_id",
        Cell: ({ value }) => renderIdButton(value),
      },
      {
        id: "side",
        title: "Side",
        accessor: formatSide,
      },
      {
        title: "Base",
        accessor: "base_amount",
        align: "right",
        Cell: ({ value, row }) => (
          <span>
            {formatUnits(value, 18, 6)} {row.original.asset}
          </span>
        ),
      },
      {
        title: "Quote",
        accessor: "quote_amount",
        align: "right",
        valueExtractor: (value) => `${formatUnits(value, 6, 2)} USDC`,
      },
      {
        title: "Attempts",
        accessor: "attempts",
        align: "right",
        valueExtractor: (value) => String(value ?? 0),
      },
      {
        title: "Error",
        accessor: "last_error",
        Cell: ({ value }) => <span>{value || "-"}</span>,
      },
      {
        title: "Action",
        accessor: "hedge_id",
        disableSortBy: true,
        Cell: ({ value }) => renderRepairActions(value, true),
      },
    ],
    [renderIdButton, renderRepairActions]
  );

  const summary = useMemo(() => {
    if (swapsError) {
      return "Swap history unavailable";
    }
    const succeeded = swaps.filter(
      (swap) => swap.hedge_status === "succeeded"
    ).length;
    const failed = swaps.filter(
      (swap) => swap.hedge_status === "failed"
    ).length;
    const pending = swaps.filter(
      (swap) => swap.hedge_status === "pending"
    ).length;
    return `${swaps.length} loaded | ${succeeded} succeeded | ${pending} pending | ${failed} failed`;
  }, [swaps, swapsError]);

  const pendingHedgeCount = inventory?.pending_hedge_count ?? 0;
  const failedHedgeCount = inventory?.failed_hedge_count ?? 0;

  return (
    <PageWrapper>
      {isMobileScreen ? <MobileDirectory /> : null}
      <HeaderRow>
        <div>
          <PageTitle>Ondo RFQ</PageTitle>
          <PageSubtitle>
            Review Aevo L2 Ondo swaps and repair failed mainnet hedges.
          </PageSubtitle>
        </div>
        <Button buttonTheme={ButtonThemeEnum.NEUTRAL2} onClick={onRefresh}>
          Refresh
        </Button>
      </HeaderRow>

      <OverviewWrapper>
        <TableLoader
          isLoading={
            isLoadingInventory ||
            isValidatingInventory ||
            isLoadingHedgerBalances ||
            isValidatingHedgerBalances
          }
        />
        <OverviewHeader>
          <SectionTitleStack>
            <SectionTitle>Inventory</SectionTitle>
            <MutedText>
              Balances available for Aevo fills and Ethereum hedging.
            </MutedText>
          </SectionTitleStack>
          <HealthChips>
            <StatusBadge status="pending">
              Pending {pendingHedgeCount}
            </StatusBadge>
            <StatusBadge status={failedHedgeCount > 0 ? "failed" : "succeeded"}>
              Failed {failedHedgeCount}
            </StatusBadge>
          </HealthChips>
        </OverviewHeader>
        <InventoryColumns>
          <InventoryPanel>
            <InventoryPanelHeader>
              <NetworkIdentity>
                <NetworkMark>L2</NetworkMark>
                <div>
                  <NetworkName>Aevo L2</NetworkName>
                  <NetworkDescription>Spot counterparty</NetworkDescription>
                </div>
              </NetworkIdentity>
              <AccountMeta>
                <MutedText>Chain 2999</MutedText>
                {renderIdButton(inventory?.counterparty)}
              </AccountMeta>
            </InventoryPanelHeader>
            {inventoryError ? (
              <ErrorText>{inventoryErrorMessage}</ErrorText>
            ) : null}
            <InventoryListHeader>
              <span>Asset</span>
              <span>Balance</span>
            </InventoryListHeader>
            <InventoryList>
              <InventoryRow>
                <InventoryAsset>
                  {renderAssetLogo("USDC")}
                  <AssetDetails>
                    <AssetName>USDC</AssetName>
                    <AssetMeta>Shared settlement inventory</AssetMeta>
                  </AssetDetails>
                </InventoryAsset>
                <InventoryBalance>
                  {formatBalanceWithSymbol(sharedUsdcBalance, "USDC")}
                </InventoryBalance>
              </InventoryRow>
              {inventoryRows.map((balance) => (
                <InventoryRow key={balance.collateral}>
                  <InventoryAsset>
                    {renderAssetLogo(balance.collateral)}
                    <AssetDetails>
                      <AssetName>{balance.collateral}</AssetName>
                      <AssetMeta>Spot inventory</AssetMeta>
                    </AssetDetails>
                  </InventoryAsset>
                  <InventoryBalance>
                    {formatBalanceWithSymbol(
                      balance.base_balance,
                      balance.collateral
                    )}
                  </InventoryBalance>
                </InventoryRow>
              ))}
            </InventoryList>
          </InventoryPanel>

          <InventoryPanel>
            <InventoryPanelHeader>
              <NetworkIdentity>
                <NetworkMark>L1</NetworkMark>
                <div>
                  <NetworkName>Ethereum Mainnet</NetworkName>
                  <NetworkDescription>Hedge executor</NetworkDescription>
                </div>
              </NetworkIdentity>
              <AccountMeta>
                <MutedText>Chain 1</MutedText>
                {renderIdButton(ONDO_HEDGER_ADDRESS)}
              </AccountMeta>
            </InventoryPanelHeader>
            {hedgerBalancesError ? (
              <ErrorText>{hedgerBalancesError.message}</ErrorText>
            ) : null}
            <InventoryListHeader>
              <span>Asset</span>
              <span>Balance</span>
            </InventoryListHeader>
            <InventoryList>
              {sortedHedgerBalances.map((balance) => (
                <InventoryRow key={`hedger-${balance.symbol}`}>
                  <InventoryAsset>
                    {renderAssetLogo(balance.symbol)}
                    <AssetDetails>
                      <AssetName>{balance.symbol}</AssetName>
                      <AssetMeta>
                        {hedgerInventoryLabel(balance.symbol)}
                      </AssetMeta>
                    </AssetDetails>
                  </InventoryAsset>
                  <InventoryBalance>
                    {formatBalanceWithSymbol(balance.balance, balance.symbol)}
                  </InventoryBalance>
                </InventoryRow>
              ))}
            </InventoryList>
          </InventoryPanel>
        </InventoryColumns>
        {pausedAssetEntries.length > 0 ? (
          <PausedAssetsBanner>
            <PausedAssetsTitle>Paused Assets</PausedAssetsTitle>
            <PausedAssetsList>
              {pausedAssetEntries.map(([pausedAsset, reason]) => (
                <PausedAssetRow key={pausedAsset}>
                  <PausedAssetText>
                    <PausedAssetName>{pausedAsset}</PausedAssetName>
                    <PausedAssetReason>{reason || "-"}</PausedAssetReason>
                  </PausedAssetText>
                  <Button
                    buttonTheme={ButtonThemeEnum.NEUTRAL2}
                    onClick={() => onResumePausedAsset(pausedAsset)}
                    style={{ minHeight: 28, minWidth: 72, padding: "4px 8px" }}
                  >
                    Resume
                  </Button>
                </PausedAssetRow>
              ))}
            </PausedAssetsList>
          </PausedAssetsBanner>
        ) : null}
      </OverviewWrapper>

      <FiltersWrapper>
        <Field>
          Asset
          <Input
            value={asset}
            onChange={(event) => setAsset(event.target.value)}
            placeholder="All assets"
          />
        </Field>
        <Field>
          Status
          <Select
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as OndoHedgeStatus)
            }
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          History Limit
          <Input
            type="number"
            min={1}
            max={500}
            value={limit}
            onChange={onLimitChange}
          />
        </Field>
        <Field>
          Pause Reason
          <Input
            value={pauseReason}
            onChange={(event) => setPauseReason(event.target.value)}
          />
        </Field>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          disabled={!asset.trim()}
          onClick={onPauseAsset}
        >
          Pause Asset
        </Button>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          disabled={!asset.trim()}
          onClick={onResumeAsset}
        >
          Resume Asset
        </Button>
      </FiltersWrapper>

      <Section>
        <SectionHeader>
          <SectionTitle>Failed Hedge Repair</SectionTitle>
          <MutedText>{failedHedges.length} failed hedges</MutedText>
        </SectionHeader>
        {failedHedgesError ? (
          <ErrorText>{failedHedgesErrorMessage}</ErrorText>
        ) : null}
        {failedHedges.length === 0 &&
        !isLoadingFailedHedges &&
        !isValidatingFailedHedges ? (
          <EmptyState>No failed Ondo hedges.</EmptyState>
        ) : (
          <OndoTable
            columns={failedHedgeColumns}
            data={failedHedges}
            emptyMessage="No failed Ondo hedges."
            isLoading={isLoadingFailedHedges || isValidatingFailedHedges}
          />
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitleStack>
            <SectionTitle>Recent Swaps</SectionTitle>
            <MutedText>{summary}</MutedText>
          </SectionTitleStack>
          <PaginationControls>
            <MutedText>
              {swapRangeStart}-{swapRangeEnd} of {swaps.length}
            </MutedText>
            <PaginationSelect
              value={swapPageSize}
              onChange={onSwapPageSizeChange}
            >
              {SWAP_PAGE_SIZE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value} / page
                </option>
              ))}
            </PaginationSelect>
            <PaginationRow>
              <PaginationButton
                type="button"
                disabled={swapPage <= 1}
                onClick={() => setSwapPage((page) => Math.max(1, page - 1))}
              >
                Prev
              </PaginationButton>
              <MutedText>
                Page {swapPage} / {swapPageCount}
              </MutedText>
              <PaginationButton
                type="button"
                disabled={swapPage >= swapPageCount}
                onClick={() =>
                  setSwapPage((page) => Math.min(swapPageCount, page + 1))
                }
              >
                Next
              </PaginationButton>
            </PaginationRow>
          </PaginationControls>
        </SectionHeader>
        {swapsError ? (
          <ErrorText>
            Ondo swap history unavailable: {swapsErrorMessage}
          </ErrorText>
        ) : null}
        <OndoTable
          columns={swapColumns}
          data={paginatedSwaps}
          emptyMessage={
            swapsError
              ? "Fix the history query before relying on this list."
              : "No Ondo swaps match the selected filters."
          }
          isLoading={isLoadingSwaps || isValidatingSwaps}
        />
      </Section>
    </PageWrapper>
  );
}
